import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { CalendarEventView, CalendarProposal } from '../api/types.js';
import { useCalendarProposals, useDecideProposal } from '../hooks/use-calendar-proposals.js';
import { Button } from './button.js';
import { SettingsSection } from './settings-section.js';

// « Calendar changes »: what the server's sessions asked to change in the operator's calendar (ADR-024). Nothing
// changes until approved here. Each card says exactly what will happen; the time is the line that matters most, so
// it carries the change: the old time struck, the new one beside it. Event texts are untrusted: plain text only.

const ZONE = 'Europe/Paris';
type Time = { date?: string; dateTime?: string; timeZone?: string };

const EDGE: Record<CalendarProposal['action'], string> = { create: 'var(--color-brand)', update: 'var(--color-warn)', delete: 'var(--color-error)' };
const VERB: Record<CalendarProposal['action'], string> = { create: 'Add', update: 'Change', delete: 'Delete' };
const APPROVE: Record<CalendarProposal['action'], string> = { create: 'Add to calendar', update: 'Apply change', delete: 'Delete event' };
const DONE: Record<CalendarProposal['action'], string> = { create: 'Added to the calendar.', update: 'Changed in the calendar.', delete: 'Deleted from the calendar.' };

const isDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
const day = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: ZONE });
const dayUtc = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const hour = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: ZONE });

/** « Thu 9 Oct, 14:00–15:00 », « Mon 12 Oct, all day », across days when it spans them */
export function span(start?: string, end?: string, zone?: string): string {
  if (!start) return '';
  if (isDate(start)) {
    const first = dayUtc.format(new Date(`${start}T00:00:00Z`));
    // Google's all-day end is exclusive
    const lastDay = end && isDate(end) ? new Date(Date.parse(`${end}T00:00:00Z`) - 86_400_000) : null;
    const last = lastDay ? dayUtc.format(lastDay) : first;
    return last === first ? `${first}, all day` : `${first} to ${last}, all day`;
  }
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const sameDay = e && day.format(s) === day.format(e);
  const range = e ? (sameDay ? `${day.format(s)}, ${hour.format(s)}–${hour.format(e)}` : `${day.format(s)}, ${hour.format(s)} to ${day.format(e)}, ${hour.format(e)}`) : `${day.format(s)}, ${hour.format(s)}`;
  return zone && zone !== ZONE ? `${range} (Paris time; event in ${zone})` : range;
}

const timeOf = (t?: Time) => t?.dateTime ?? t?.date;

/** The event as it will be after the change */
function after(p: CalendarProposal): CalendarEventView {
  const c = p.change ?? {};
  return {
    ...p.before,
    ...(c.summary !== undefined ? { summary: c.summary } : {}),
    ...(c.description !== undefined ? { description: c.description } : {}),
    ...(c.location !== undefined ? { location: c.location } : {}),
    ...(c.start ? { start: timeOf(c.start), timeZone: c.start.timeZone ?? p.before?.timeZone } : {}),
    ...(c.end ? { end: timeOf(c.end) } : {}),
    ...(c.attendees ? { attendees: c.attendees.map((a) => ({ email: a.email })) } : {}),
  };
}

function Field({ label, now, was }: { label: string; now?: string; was?: string }) {
  if (!now && !was) return null;
  const changed = was !== undefined && was !== now;
  return (
    <div className="grid grid-cols-[4.5rem_1fr] gap-x-3 text-sm sm:grid-cols-[6rem_1fr]">
      <dt style={{ color: 'var(--color-text-3)' }}>{label}</dt>
      <dd className="min-w-0 break-words" style={{ color: 'var(--color-text)' }}>
        {changed && was && <s className="mr-2" style={{ color: 'var(--color-text-3)' }}>{was}</s>}
        {now ? <span className={changed ? 'rounded-sm px-0.5' : undefined} style={changed ? { backgroundColor: 'var(--color-warn-dim)', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' } : undefined}>{now}</span> : changed && <span style={{ color: 'var(--color-text-3)' }}>(removed)</span>}
      </dd>
    </div>
  );
}

function Outcome({ p }: { p: CalendarProposal }) {
  if (p.status === 'applied') {
    const link = p.result?.link;
    return (
      <p className="text-sm" style={{ color: 'var(--color-success)' }}>
        {DONE[p.action]}{' '}
        {link && <a href={link} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>Open in Google Calendar</a>}
      </p>
    );
  }
  if (p.status === 'rejected') return <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Rejected: the calendar is unchanged.</p>;
  if (p.status === 'failed') return <p className="text-sm" style={{ color: 'var(--color-error)' }}>Approved, but Google refused it: {p.error}</p>;
  if (p.status === 'applying') return <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Applying…</p>;
  return null;
}

/** How long the session's calendar tool waits for the decision, with a margin: keep it equal to TOOL_WAIT_MS in
 * src/services/calendar-proposals.ts. The list is read every 15 s, so the time it is compared with stays current. */
const TOOL_WAIT_MS = 5 * 60_000 + 30_000;

export function CalendarProposalCard({ serverId, p, isAdmin, focused, onDecided, now, inSession = false }: {
  serverId: string; p: CalendarProposal; isAdmin: boolean; focused: boolean; onDecided: (id: string) => void; now: number; inSession?: boolean;
}) {
  const decide = useDecideProposal(serverId);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [focused]);

  const next = p.action === 'delete' ? p.before ?? {} : after(p);
  const was = p.before;
  const title = next.summary || was?.summary || '(no title)';
  const newTime = span(next.start, next.end, next.timeZone);
  const oldTime = was ? span(was.start, was.end, was.timeZone) : '';
  const moved = p.action === 'update' && oldTime && oldTime !== newTime;
  const attendees = next.attendees?.map((a) => a.email).join(', ');
  const wasAttendees = was?.attendees?.map((a) => a.email).join(', ');
  const error = decide.error as (Error & { status?: number }) | null;
  // Past the tool's wait, the session went on: a decision now reaches it as a new message (#918)
  const stoppedAt = Date.parse(p.createdAt) + TOOL_WAIT_MS;
  const stopped = !!p.requestedBySession && Number.isFinite(stoppedAt) && now >= stoppedAt;

  return (
    <article ref={ref} id={`calendar-${p.proposalId}`} aria-labelledby={`calendar-${p.proposalId}-title`}
      className="card px-4 py-3"
      style={{ borderLeft: `3px solid ${EDGE[p.action]}`, ...(focused ? { outline: '2px solid var(--color-brand-light)', outlineOffset: '3px' } : {}) }}>
      <h3 id={`calendar-${p.proposalId}-title`} className="text-sm font-semibold break-words" style={{ color: 'var(--color-text)' }}>
        {VERB[p.action]} <span className={p.action === 'delete' ? 'line-through' : undefined}>{title}</span>
      </h3>

      {/* The time carries the change: the old one struck on its own line, the new one below it */}
      {moved && (
        <p className="mt-1 text-sm tabular-nums" style={{ color: 'var(--color-text-3)' }}>
          <span className="sr-only">Was </span><s>{oldTime}</s>
        </p>
      )}
      <p className={`${moved ? 'mt-0' : 'mt-1'} text-base tabular-nums`} style={{ color: 'var(--color-text)' }}>
        {moved && <span className="sr-only">Becomes </span>}
        <span className={p.action === 'delete' ? 'line-through' : undefined} style={p.action === 'delete' ? { color: 'var(--color-text-3)' } : undefined}>{newTime || oldTime}</span>
      </p>

      <dl className="mt-2 space-y-1">
        {p.action === 'update' && p.change?.summary !== undefined && p.change.summary !== was?.summary && <Field label="Title" now={next.summary} was={was?.summary} />}
        <Field label="Where" now={next.location} was={p.action === 'update' ? was?.location : undefined} />
        <Field label="Invites" now={attendees} was={p.action === 'update' ? wasAttendees : undefined} />
        <Field label="Notes" now={next.description && next.description.length > 300 ? `${next.description.slice(0, 300)}…` : next.description}
          was={p.action === 'update' && p.change?.description !== undefined ? was?.description?.slice(0, 300) : undefined} />
      </dl>

      {(attendees || wasAttendees) && (
        <p className="mt-2 text-xs" style={{ color: 'var(--color-text-2)' }}>
          {p.notifyAttendees ? 'The attendees get an email from Google about this.' : 'The attendees are not emailed.'}
        </p>
      )}
      {(p.reason || p.requestedBySession) && (
        <p className="mt-1 text-xs break-words" style={{ color: 'var(--color-text-2)' }}>
          {p.reason && <>Why: {p.reason}. </>}
          {p.requestedBySession && !inSession && <>Asked by <Link to={`/servers/${serverId}/sessions/${p.requestedBySession}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>a session</Link>.</>}
        </p>
      )}

      <div className="mt-3">
        {p.status === 'pending' && !decide.isSuccess ? (
          isAdmin ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" className="max-sm:min-h-10" variant={p.action === 'delete' ? 'danger' : 'primary'} disabled={decide.isPending}
                onClick={() => decide.mutate({ proposalId: p.proposalId, decision: 'approve' }, { onSuccess: () => onDecided(p.proposalId) })}>
                {decide.isPending && decide.variables?.decision === 'approve' ? 'Applying…' : APPROVE[p.action]}
              </Button>
              <Button size="sm" className="max-sm:min-h-10" variant="ghost" disabled={decide.isPending} onClick={() => decide.mutate({ proposalId: p.proposalId, decision: 'reject' }, { onSuccess: () => onDecided(p.proposalId) })}>
                Reject
              </Button>
              {stopped
                ? <span className="text-xs" style={{ color: 'var(--color-warn)' }}>{`The session stopped waiting at ${new Date(stoppedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. Decide anyway: it is told.`}</span>
                : p.requestedBySession && <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>The session waits for your answer.</span>}
            </div>
          ) : (
            <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Waiting for an admin of this server to decide.</p>
          )
        ) : (
          <Outcome p={decide.data?.proposal ?? p} />
        )}
        {error && <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--color-error)' }}>Not decided: {error.message}</p>}
      </div>
    </article>
  );
}

function decidedLine(p: CalendarProposal): string {
  const what = `${VERB[p.action]} ${(p.action === 'create' ? p.change?.summary : p.before?.summary) || 'an event'}`;
  const how = p.status === 'applied' ? 'applied' : p.status === 'rejected' ? 'rejected' : p.status === 'failed' ? `failed: ${p.error ?? ''}` : p.status;
  return `${what}, ${how}`;
}

function DecidedLine({ p, focused }: { p: CalendarProposal; focused: boolean }) {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [focused]);
  return (
    <li ref={ref} id={`calendar-${p.proposalId}`} className="w-fit max-w-full rounded-sm px-1 text-xs break-words"
      style={{ color: p.status === 'failed' ? 'var(--color-error)' : 'var(--color-text-2)', ...(focused ? { outline: '2px solid var(--color-brand-light)', outlineOffset: '3px' } : {}) }}>
      {decidedLine(p)}
    </li>
  );
}

/**
 * `pending`: the proposals to decide (main column), nothing when none waits. `history`: the decided ones of the last
 * 7 days (side column). Both by default.
 */
export function CalendarChanges({ serverId, isAdmin, part = 'all' }: { serverId: string; isAdmin: boolean; part?: 'all' | 'pending' | 'history' }) {
  const { data, dataUpdatedAt } = useCalendarProposals(serverId);
  const [params] = useSearchParams();
  const focus = params.get('calendar');
  // Decided here: the card keeps its outcome in view; any other decided proposal is a line in the history
  const [decidedHere, setDecidedHere] = useState<Set<string>>(() => new Set());
  const focusIsDecided = !!data?.some((p) => p.proposalId === focus && p.status !== 'pending' && p.status !== 'applying' && !decidedHere.has(p.proposalId));
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    if (focusIsDecided) setHistoryOpen(true);
  }, [focusIsDecided]);
  if (!data || data.length === 0) return null;
  // A session's proposals are decided in that session (ADR-039 8c); one still opened by its old link stays here
  const pending = data.filter((p) => (p.status === 'pending' || p.status === 'applying' || decidedHere.has(p.proposalId)) && (!p.requestedBySession || p.proposalId === focus));
  const decided = data.filter((p) => !pending.includes(p));
  const markDecided = (id: string) => setDecidedHere((prev) => new Set(prev).add(id));
  if (part === 'pending' && pending.length === 0) return null;
  if (part === 'history' && decided.length === 0) return null;
  const showPending = part !== 'history';
  const showHistory = part !== 'pending';
  const history = showHistory && decided.length > 0 && (
    <details className="mt-3" open={historyOpen} onToggle={(e) => setHistoryOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary className="cursor-pointer py-1.5 text-xs max-sm:min-h-10" style={{ color: 'var(--color-text-3)' }}>
        Decided in the last 7 days ({decided.length})
      </summary>
      <ul className="mt-2 space-y-1">
        {decided.map((p) => <DecidedLine key={p.proposalId} p={p} focused={historyOpen && p.proposalId === focus} />)}
      </ul>
    </details>
  );

  // Settings: the same section pattern as the tab's other sections
  if (part === 'history') {
    return (
      <SettingsSection id="calendar-title-history" title="Calendar changes"
        state="Changes to your calendar asked by this server's sessions, once you approved or rejected them.">
        {history}
      </SettingsSection>
    );
  }

  return (
    <section aria-labelledby="calendar-title" className="mb-8 min-w-0">
      <h2 id="calendar-title" className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Calendar changes</h2>
      {showPending && (
        <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-2)' }}>
          {pending.some((p) => p.status === 'pending')
            ? 'Asked by this server\'s sessions. Nothing changes in your calendar until you approve it here.'
            : 'Nothing waits for your decision.'}
        </p>
      )}
      {showPending && pending.length > 0 && (
        <div className="mt-3 space-y-3">
          {pending.map((p) => <CalendarProposalCard key={p.proposalId} serverId={serverId} p={p} isAdmin={isAdmin} focused={p.proposalId === focus} onDecided={markDecided} now={dataUpdatedAt} />)}
        </div>
      )}
      {history}
    </section>
  );
}
